import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import PermissionGate from '../../../components/common/PermissionGate';
import communicationLogService from '../../../api/services/communicationLogService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import {
  MessageSquare,
  Search,
  Filter,
  Download,
  Eye,
  Mail,
  Phone,
  Calendar,
  User,
  Building,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Send,
  Reply,
  Forward,
  Plus,
  Edit,
  Trash2,
  FileText,
  Paperclip,
  MoreVertical,
  X,
  Square,
  CheckSquare,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';


const OfferCommunicationLogs = ({ onViewCommunication }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, canUpdate, canCreate, canDelete } = usePermissions();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || 'all');
  const [dateRange, setDateRange] = useState({ 
    startDate: searchParams.get('startDate') || '', 
    endDate: searchParams.get('endDate') || '' 
  });
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [showNewCommunication, setShowNewCommunication] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: Number(searchParams.get('page')) || 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: Number(searchParams.get('limit')) || 10
  });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ 
    key: searchParams.get('sortBy') || 'sentDate', 
    direction: searchParams.get('sortOrder') || 'desc' 
  });
  const isInitialMountRef = useRef(true);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);


  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sync filter state to URL search params
  useLayoutEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'priority', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterPriority && filterPriority !== 'all') params.set('priority', filterPriority);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (pagination.currentPage && pagination.currentPage !== 1) params.set('page', String(pagination.currentPage));
      if (pagination.itemsPerPage && pagination.itemsPerPage !== 10) params.set('limit', String(pagination.itemsPerPage));
      if (sortConfig.key) params.set('sortBy', sortConfig.key);
      if (sortConfig.direction && sortConfig.direction !== 'desc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterPriority, dateRange, pagination.currentPage, pagination.itemsPerPage, sortConfig, setSearchParams]);

  // Reset to first page on filter or date range change
  useEffect(() => {
    // Skip page reset during initial mount to preserve pagination from URL
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setSelectedRows(new Set()); // Clear selection when filters change
  }, [filterStatus, filterPriority, dateRange.startDate, dateRange.endDate, searchTerm]);

  // Fetch communication logs using React Query
  const {
    data: communicationsData,
    isLoading: loading,
    error: communicationsError,
    refetch: refetchCommunications
  } = useQuery({
    queryKey: ['communicationLogs', pagination.currentPage, debouncedSearchTerm, filterStatus, filterPriority, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterPriority !== 'all' && { priority: filterPriority }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      };

      const result = await communicationLogService.getCommunicationLogs(params);

      if (result && (result.responseCode === 200 || result.responseCode === 201)) {
        return {
          communications: result.data.communications || [],
          stats: result.data.stats || {
            total: 0,
            totalSent: 0,
            failed: 0,
            pending: 0,
            accepted: 0,
            followUps: 0
          },
          pagination: result.data.pagination || pagination
        };
      } else {
        throw new Error(result?.responseMessage || 'Failed to load communication logs');
      }
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
      showError(error.message || 'Error loading communication logs');
    }
  });

  const communications = communicationsData?.communications || [];
  const stats = communicationsData?.stats || {
    total: 0,
    totalSent: 0,
    failed: 0,
    pending: 0,
    accepted: 0,
    followUps: 0
  };

  // Frontend-only sorting logic
  const sortedCommunications = React.useMemo(() => {
    let sortableItems = [...communications];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Case-insensitive sorting for strings
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        // Specific handling for dates if needed (sentDate is already sortable as string or date)
        if (sortConfig.key === 'sentDate') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [communications, sortConfig]);

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = debouncedSearchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== 'all') f.status = filterStatus;
    if (filterPriority !== 'all') f.priority = filterPriority;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    return f;
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterPriority,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const exportSortBy = useMemo(() => sortConfig.key || 'sentDate', [sortConfig.key]);
  const exportSortOrder = sortConfig.direction;

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'offer-communication-logs',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? pagination.currentPage,
            limit: overrideLimit ?? pagination.itemsPerPage,
            sortBy: exportSortBy,
            sortOrder: exportSortOrder,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `offer-communication-logs-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      pagination.currentPage,
      pagination.itemsPerPage,
      exportSortBy,
      exportSortOrder,
    ]
  );

  const handleCommunicationLogsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        showError('No selected logs to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess(`${ids.length} log(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, showSuccess, showError]
  );

  const handleCommunicationLogsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !sortedCommunications.length) {
          showError('No logs to export on the current page.');
          return;
        }

        if (scope === 'filtered' && (pagination.totalItems ?? 0) === 0) {
          showError('No logs match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            showError('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        showSuccess('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        showError(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      sortedCommunications.length,
      pagination.totalItems,
      baseExportFilters,
      requestBackendExport,
      showSuccess,
      showError,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="h-4 w-4 text-agrosaf-green" /> :
      <ArrowDown className="h-4 w-4 text-agrosaf-green" />;
  };

  // Update pagination when data changes
  useEffect(() => {
    if (communicationsData?.pagination) {
      setPagination(communicationsData.pagination);
    }
  }, [communicationsData?.pagination]);

  // Fetch communication statistics using React Query
  const {
    data: statsData,
    isLoading: loadingStats
  } = useQuery({
    queryKey: ['communicationStats'],
    queryFn: async () => {
      const result = await communicationLogService.getCommunicationStats();
      if (result && (result.responseCode === 200 || result.responseCode === 201)) {
        return result.data.overview || {
          total: 0,
          totalSent: 0,
          failed: 0,
          pending: 0,
          accepted: 0,
          followUps: 0
        };
      } else {
        throw new Error(result?.responseMessage || 'Failed to load stats');
      }
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2
  });

  // Use stats from stats endpoint if available, otherwise use stats from communications endpoint
  const finalStats = statsData || stats;

  // Delete communication mutation
  const deleteMutation = useMutation({
    mutationFn: async (communicationId) => {
      const result = await communicationLogService.deleteCommunicationLog(communicationId);
      if (result && result.responseCode === 200) {
        return result;
      } else {
        throw new Error(result?.responseMessage || 'Failed to delete communication');
      }
    },
    onSuccess: () => {
      showSuccess('Communication deleted successfully');
      setConfirmDeleteId(null);
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['communicationLogs'] });
      queryClient.invalidateQueries({ queryKey: ['communicationStats'] });
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete communication');
    }
  });

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setFilterPriority('all');
    setDateRange({ startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };



  // Handle search (no need for separate handler, setSearchTerm is used directly)

  // Handle filter change
  const handleFilterChange = (type, value) => {
    if (type === 'status') {
      setFilterStatus(value);
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Selection handlers
  const handleSelectAll = (e) => {
    const isChecked = e?.target ? e.target.checked : e;
    if (isChecked) {
      const allIds = communications.map(comm => comm._id || comm.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Permission-based access control (Dynamic RBAC)
  const canCreateCommunication = () => {
    return canCreate('recruitment');
  };

  const canEditCommunication = () => {
    return hasPermission('recruitment', 'update');
  };

  const canDeleteCommunication = () => {
    return canDelete('recruitment');
  };

  const canViewCommunication = () => {
    return hasPermission('recruitment', 'read');
  };

  const canExportLogs = () => {
    return hasPermission('recruitment', 'read');
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleNewCommunication = () => {
    sessionStorage.setItem('communicationNavContext', 'recruitment');
    navigate('/new_communication');
  };

  const handleEditCommunication = (communicationId) => {
    navigate(`/edit_communication/${communicationId}`);
  };

  const handleReplyCommunication = (communicationId) => {
    navigate(`/reply_communication/${communicationId}`);
  };

  const handleForwardCommunication = (communicationId) => {
    navigate(`/forward_communication/${communicationId}`);
  };

  const handleViewCommunication = (communicationId) => {
    if (onViewCommunication) {
      onViewCommunication(communicationId);
    } else {
      navigate(`/view_communication/${communicationId}`);
    }
  };

  // Dropdown handlers
  const toggleDropdown = (communicationId) => {
    setOpenDropdown(openDropdown === communicationId ? null : communicationId);
  };

  const handleMenuAction = (action, communicationId) => {
    setOpenDropdown(null);
    switch (action) {
      case 'view':
        handleViewCommunication(communicationId);
        break;
      case 'edit':
        handleEditCommunication(communicationId);
        break;
      case 'reply':
        handleReplyCommunication(communicationId);
        break;
      case 'forward':
        handleForwardCommunication(communicationId);
        break;
      case 'delete':
        openDeleteConfirm(communicationId);
        break;
      default:
        break;
    }
  };

  const openDeleteConfirm = (communicationId) => {
    setConfirmDeleteId(communicationId);
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const confirmDelete = () => {
    if (!confirmDeleteId) return;
    deleteMutation.mutate(confirmDeleteId);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await communicationLogService.deleteCommunicationLog(id);
        if (result && result.responseCode === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      showSuccess(`${successCount} log(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['communicationLogs'] });
      queryClient.invalidateQueries({ queryKey: ['communicationStats'] });
    } else {
      showError('Failed to delete selected communication logs');
    }
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' }
  ];


  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'received': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'accepted': return 'bg-emerald-100 text-emerald-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <Send className="h-4 w-4" />;
      case 'received': return <MessageSquare className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'accepted': return <CheckCircle className="h-4 w-4" />;
      case 'declined': return <XCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };


  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };



  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login redirect if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Offer Communication Logs</h1>
          <p className="text-gray-600 mt-1">Track and manage all offer-related communications</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          {canExportLogs() && (
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-agrosaf-green border-t-transparent" /> : <Download className="h-4 w-4" />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleExportClick();
                }}
                disabled={exportLoading}
                className={`cursor-pointer ${selectedRows.size > 0 ? 'bg-green-50 border-green-200 text-green-700' : ''}`}
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleCommunicationLogsSelectedFormatExport}
              />
            </div>
          )}
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleNewCommunication}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              New Communication
            </Button>
          </PermissionGate>
        </div>
      </div>



      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Communications</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(finalStats.total)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Total Sent */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Sent</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(finalStats.totalSent)}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Send className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        {/* Failed */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(finalStats.failed)}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidate name..."
                value={searchTerm}
                maxLength={100}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPagination(prev => ({ ...prev, currentPage: 1 }));
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => {
              setFilterPriority(e.target.value);
              setPagination(prev => ({ ...prev, currentPage: 1 }));
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {priorityOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            />
          </div>

          {(searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || dateRange.startDate || dateRange.endDate) && (
            <div className="relative">
              <button
                onClick={handleClearFilters}
                className="w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-1 whitespace-nowrap"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Communications Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <div
                    className="cursor-pointer text-gray-400 hover:text-agrosaf-green transition-colors"
                    onClick={() => {
                      const isAllPageSelected = sortedCommunications.length > 0 && sortedCommunications.every(comm => selectedRows.has(comm._id || comm.id));
                      handleSelectAll(!isAllPageSelected);
                    }}
                  >
                    {sortedCommunications.length > 0 && sortedCommunications.every(comm => selectedRows.has(comm._id || comm.id)) ? (
                      <CheckSquare className="h-5 w-5 text-agrosaf-green" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left hover:bg-gray-100 select-none cursor-pointer" onClick={() => handleSort('candidateName')}>
                  <div className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Candidate</span>
                    {getSortIndicator('candidateName')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left hover:bg-gray-100 select-none cursor-pointer" onClick={() => handleSort('subject')}>
                  <div className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Subject</span>
                    {getSortIndicator('subject')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left hover:bg-gray-100 select-none cursor-pointer" onClick={() => handleSort('status')}>
                  <div className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Status</span>
                    {getSortIndicator('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left hover:bg-gray-100 select-none cursor-pointer" onClick={() => handleSort('priority')}>
                  <div className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Priority</span>
                    {getSortIndicator('priority')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left hover:bg-gray-100 select-none cursor-pointer" onClick={() => handleSort('sentDate')}>
                  <div className="flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Date</span>
                    {getSortIndicator('sentDate')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
                      <span className="ml-2 text-gray-600">Loading communications...</span>
                    </div>
                  </td>
                </tr>
              ) : sortedCommunications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No communications found
                  </td>
                </tr>
              ) : (
                sortedCommunications.map((communication) => (
                  <tr
                    key={communication._id || communication.id}
                    className={`hover:bg-gray-50 transition-colors ${selectedRows.has(communication._id || communication.id) ? 'bg-green-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className="cursor-pointer text-gray-400 hover:text-agrosaf-green transition-colors"
                        onClick={() => handleSelectRow(communication._id || communication.id)}
                      >
                        {selectedRows.has(communication._id || communication.id) ? (
                          <CheckSquare className="h-5 w-5 text-agrosaf-green" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-medium">
                          {communication.candidateName?.charAt(0)}
                        </div>
                        <div className="ml-3 max-w-[200px]">
                          <div className="text-sm font-medium text-gray-900 capitalize truncate">
                            {communication.candidateName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={communication.subject}>
                        {communication.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(communication.status)}`}>
                        {getStatusIcon(communication.status)}
                        <span className="ml-1 capitalize">{communication.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getPriorityColor(communication.priority)}`}>
                        {communication.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-sm font-medium">
                        {communication.sentDate
                          ? formatDate(communication.sentDate, { includeTime: false })
                          : 'N/A'}
                      </div>
                      {communication.sentTime && (
                        <div className="text-xs text-gray-500">{communication.sentTime}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <PermissionGate permission="recruitment" action="read">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewCommunication(communication._id || communication.id);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                            title="View Communication"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="recruitment" action="delete">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openDeleteConfirm(communication._id || communication.id);
                            }}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-100 transition-colors"
                            title="Delete Communication"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <PaginationControls
            currentPage={pagination.currentPage || 1}
            totalPages={pagination.totalPages || 1}
            totalItems={pagination.totalItems || 0}
            limit={pagination.itemsPerPage}
            onPageChange={handlePageChange}
            hasNextPage={pagination.currentPage < pagination.totalPages}
            hasPrevPage={pagination.currentPage > 1}
          />
        </div>
      )}

      {/* Empty State */}
      {communications.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No communications found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus !== 'all' || filterPriority !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'No offer communications have been recorded yet.'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && filterPriority === 'all' && (
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleNewCommunication}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                New Communication
              </Button>
            </PermissionGate>
          )}
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        alertState={alertState}
        hideAlert={hideAlert}
      />

      {/* Confirm Delete Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Communication</h3>
            <p className="text-sm text-gray-700 mb-6">Are you sure you want to delete this communication? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={cancelDelete} disabled={deleteMutation.isPending}>
                No, Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Selected Logs
              </h3>
              <p className="text-gray-600 text-center mb-2">
                You are about to permanently delete{' '}
                <span className="font-semibold text-red-600">{selectedRows.size} communication log(s)</span>.
              </p>
              <p className="text-sm text-gray-500 text-center mb-6">
                This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="flex-1"
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </Button>
                <PermissionGate permission="recruitment" action="delete">
                  <Button
                    variant="danger"
                    onClick={handleBulkDelete}
                    className="flex-1"
                    loading={bulkDeleteLoading}
                  >
                    Delete {selectedRows.size} Log(s)
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Communication Logs"
        showDateRange
        scopedFormatExport={{
          onExport: handleCommunicationLogsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div>
  );
};

export default OfferCommunicationLogs;
